const STORAGE_KEY = 'PULSE_HOUSTON_COLLECTOR';
/**
 * Fetches collector configuration from the remote endpoint
 */
const fetchCollector = async (env) => {
    const collectorPath = `https://www.${'subito' }.it/config/collector.json`;
    const response = await fetch(collectorPath);
    if (!response.ok) {
        throw new Error(`Failed to fetch collector config: ${response.status} ${response.statusText}`);
    }
    const data = await response.json();
    // Store in cache
    try {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(data));
    }
    catch (error) {
        console.warn('Error storing collector data:', error);
    }
    return data.default;
};
/**
 * Retrieves the Houston collector configuration, utilizing local storage caching and a
 * stale-while-revalidate strategy.
 *
 * - If a valid cached configuration is found in localStorage, it is returned immediately,
 *   and a background refresh is triggered to update the cache.
 * - If no valid cache is found, a fresh configuration is fetched from the remote endpoint.
 * - If all attempts fail, a default collector configuration is returned.
 *
 * @param env - The environment identifier ('dev', 'stage', or 'prod').
 * @returns A promise that resolves to the collector configuration.
 *
 * @example
 * ```ts
 * const collector = await getHoustonCollector('prod');
 * console.log(collector); // { datacollector: 'https://collector.subito.it/api/v1/track', cis: 'https://cis.mpianalytics.com' }
 * ```
 */
const getHoustonCollector = async (env) => {
    /**
     * Default collector configuration used as a fallback if fetching or parsing fails.
     */
    const defaultCollector = {
        datacollector: 'https://collector.subito.it/api/v1/track',
        cis: 'https://cis.mpianalytics.com',
    };
    try {
        // Attempt to retrieve cached collector configuration from localStorage
        const cachedItem = localStorage.getItem(STORAGE_KEY);
        if (cachedItem) {
            try {
                // Parse and return cached configuration immediately
                const cachedData = JSON.parse(cachedItem);
                // Trigger background refresh to update cache (stale-while-revalidate)
                fetchCollector(env).catch((error) => {
                    console.warn('Background cache refresh failed:', error);
                });
                return cachedData.default;
            }
            catch (error) {
                // If parsing fails, log warning and proceed to fetch fresh data
                console.warn('Error parsing stored collector:', error);
            }
        }
        // No valid cache found; fetch fresh configuration from remote endpoint
        return await fetchCollector(env);
    }
    catch (error) {
        // If all attempts fail, log error and return default configuration
        console.error('Error getting Houston collector:', error);
        return defaultCollector;
    }
};

export { getHoustonCollector };
//# sourceMappingURL=getHoustonCollector.js.map
